import { Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import BodyContourOperationContent from '../bodyContour/BodyContourOperationContent';
import SuvThresholdOperationContent from '../suvThreshold/SuvThresholdOperationContent';

interface ThresholdSegmentationOperationContentProps {
    onClose: () => void;
    onPreventCloseChange: (prevented: boolean) => void;
}

/**
 * Threshold segmentation: one operation over whichever intensity unit
 * the primary image is quantified in.
 *
 * The unit is a property of the loaded image, not a choice — a series is
 * HU-quantified or SUVbw-quantified, never both — so the presets that
 * can run are selected here rather than offered as tabs the user would
 * have to pick between. The HU presets (Body, Bone, High density) keep
 * their own tab strip; SUVbw has a single preset and needs none.
 *
 * Both branches share the toolbar entry, the panel, the pipeline, the
 * region overlay and the output resolution; they differ in the unit the
 * threshold is expressed in — absolute HU against the image, a
 * percentage of the region's own SUVmax — which is why they remain two
 * bodies rather than one parameterised form.
 */
export default function ThresholdSegmentationOperationContent({
    onClose,
    onPreventCloseChange,
}: ThresholdSegmentationOperationContentProps) {
    const image = useActiveEditTarget().environment?.image?.parent;

    if (image?.isSuvQuantified) {
        return (
            <SuvThresholdOperationContent
                onClose={onClose}
                onPreventCloseChange={onPreventCloseChange}
            />
        );
    }
    if (image?.isHuQuantified) {
        return (
            <BodyContourOperationContent
                onClose={onClose}
                onPreventCloseChange={onPreventCloseChange}
            />
        );
    }
    // The toolbar gates on the same predicate, so reaching this means the
    // image changed under an open panel. Stating the requirement beats an
    // empty panel that looks broken.
    return (
        <Typography variant="body2" color="text.secondary">
            <FormattedMessage id="thresholdSeg.notQuantified" />
        </Typography>
    );
}
